import { PlusOutlined } from '@ant-design/icons';
import { Form, FormInstance } from 'antd';
import {
  Checkbox,
  DatePicker,
  Input,
  Select,
  Table,
} from 'UI';
import CultureSelectSearch from 'components/CultureSelectSearch';
import {
  Culture,
  Order,
  Product,
  Tray,
  Variety,
} from 'constants/interface';
import { InventoryTypeDisplayMapping, InventoryTypeEnum } from 'constants/inventory';
import { ProductTypeEnum } from 'constants/product';
import { useDispatch } from 'hooks/store';
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  cleanCulturesAction,
  getCultureProductsAction,
  setCulturesAction,
  setProductsAction,
} from 'state/slices/products';
import {
  getTrayListAction,
  isLoadingTraysSelector,
  traysEntitiesSelector,
  traysIdsSelector,
} from 'state/slices/tray';
import {
  OrderOtherStatusEnum,
  OrderStumpTypeDisplayMapping,
  OrderStumpTypeEnum,
  OrderTypeEnum,
  OrderUnitDisplayMapping,
  OrderUnitEnum,
} from 'constants/order';
import dayjs, { Dayjs } from 'dayjs';
import CultureProductSelect from 'components/CultureProductSelect';
import { calculateSeedQuantity } from 'helpers/order';

import {
  FormContainer,
  RowFormItem,
  AddRowButton,
  NotesInput,
  RowInfoContainer,
  RemoveRowButton,
  RemoveRowIcon,
  RowIndexLabel,
  RowSmallFormItem,
  TableFooterContainer,
  OrderPriceContainer,
  GraftingOrderTable,
  TableHeaderTitle,
} from './style';

interface GraftingOrderItemFormData {
  // Order Item Scope
  stumpType: OrderStumpTypeEnum
  stumpQuantity?: string
  pruning: boolean
  unit: OrderUnitEnum
  orderQuantity: string
  plannedSeedDate: Dayjs
  plannedDeliveryDate: Dayjs
  unitPrice: string
  stumpUnitPrice?: string
  totalPrice: string
  notes?: string

  // Scion Item Scope
  scionCulture: string
  scionProduct: string
  scionTray: string
  scionSeedSource: InventoryTypeEnum
  scionCalculatedSeedQuantity: string

  // Stock Item Scope
  stockCulture: string
  stockProduct: string
  stockTray: string
  stockSeedSource: InventoryTypeEnum
  stockCalculatedSeedQuantity: string
}

export interface GraftingOrderItemsFormData {
  items: GraftingOrderItemFormData[]
}

interface RowProductData {
  culture?: Culture
  variety?: Variety
  product?: Product
  tray?: Tray
}

interface RowData {
  scion?: RowProductData
  stock?: RowProductData
  totalPrice?: number
  stumpType?: OrderStumpTypeEnum
  stumpTotalPrice?: number
}

interface GraftingTableProps {
  formHandler: FormInstance<GraftingOrderItemsFormData>
  isLoading: boolean
  initialOrder?: Order
}

const createDefaultRow = () => ({
  scionSeedSource: InventoryTypeEnum.CLIENT,
  stockSeedSource: InventoryTypeEnum.CLIENT,
  unit: OrderUnitEnum.THOUSAND,
  stumpType: OrderStumpTypeEnum.DISCARD,
});

const GraftingTable = ({
  formHandler,
  isLoading: externalLoading,
  initialOrder,
}: GraftingTableProps): JSX.Element => {
  const dispatch = useDispatch();

  const [rowSelectedData, setRowSelectedData] = useState<RowData[]>([]);

  const culturesList: (Culture | undefined)[] = [];
  const productsList: (Product | undefined)[] = [];

  const [orderTotalPrice, setOrderTotalPrice] = useState(0);

  const updateOrderTotalPrice = (rowData: RowData[]) => {
    const updatedOrderTotalPrice = rowData
      .reduce((acc, curRow) => {
        const newValue = (acc || 0) + (curRow.totalPrice || 0);
        return newValue;
      }, 0);

    setOrderTotalPrice(updatedOrderTotalPrice || 0);
  };

  const memoizedInitialOrder = useMemo(() => {
    if (initialOrder) {
      return { ...initialOrder };
    }

    return undefined;
  }, [initialOrder?.id]);

  // Initial Order
  useEffect(() => {
    if (
      memoizedInitialOrder
      && memoizedInitialOrder.type === OrderTypeEnum.GRAFTING
    ) {
      const updatedRowSelectedData: RowData[] = [];
      const updatedFormItems = memoizedInitialOrder
        .items
        .map((initialOrderItem) => {
          const scionProdItem = initialOrderItem
            .productionItems
            .find((curProdItem) => curProdItem.type === ProductTypeEnum.SCION);

          const stockProdItem = initialOrderItem
            .productionItems
            .find((curProdItem) => curProdItem.type === ProductTypeEnum.STOCK);

          const updatedTotalPrice = (
            (
              (initialOrderItem.orderQuantity * initialOrderItem.unitPrice)
              + ((initialOrderItem.stumpQuantity || 0) * (initialOrderItem.stumpUnitPrice || 0))
            ) / 100
          );

          updatedRowSelectedData.push({
            scion: {
              culture: scionProdItem?.product?.variety?.culture,
              variety: scionProdItem?.product?.variety,
              product: scionProdItem?.product,
              tray: scionProdItem?.tray,
            },
            stock: {
              culture: stockProdItem?.product?.variety?.culture,
              variety: stockProdItem?.product?.variety,
              product: stockProdItem?.product,
              tray: stockProdItem?.tray,
            },
            totalPrice: updatedTotalPrice,
            stumpType: initialOrderItem.stumpType,
          });

          culturesList.push(
            scionProdItem?.product?.variety?.culture,
            stockProdItem?.product?.variety?.culture,
          );

          productsList.push(
            scionProdItem?.product,
            stockProdItem?.product,
          );

          const calculatedScionSeedQuantity = scionProdItem
            ? `${calculateSeedQuantity(
              initialOrderItem.orderQuantity,
              initialOrderItem.unit,
              scionProdItem?.product,
              scionProdItem?.tray,
            )}`
            : '0';

          const calculatedStockSeedQuantity = stockProdItem
            ? `${calculateSeedQuantity(
              initialOrderItem.orderQuantity,
              initialOrderItem.unit,
              stockProdItem?.product,
              stockProdItem?.tray,
            )}`
            : '0';

          const itemFormData = {
            pruning: initialOrderItem.pruning,
            unit: initialOrderItem.unit,
            orderQuantity: `${initialOrderItem.orderQuantity}`,
            stumpQuantity: `${initialOrderItem.stumpQuantity}`,
            plannedSeedDate: dayjs(initialOrderItem.plannedSeedDate),
            plannedDeliveryDate: dayjs(initialOrderItem.plannedDeliveryDate),
            unitPrice: `${initialOrderItem.unitPrice / 100}`,
            stumpUnitPrice: `${(initialOrderItem.stumpUnitPrice || 0) / 100}`,
            notes: initialOrderItem.notes,

            scionCulture: scionProdItem?.product.variety.culture.id,
            scionVariety: scionProdItem?.product.variety.id,
            scionProduct: scionProdItem?.product.id,
            scionTray: scionProdItem?.tray.id,
            scionSeedSource: scionProdItem?.seedSource,
            scionCalculatedSeedQuantity: calculatedScionSeedQuantity,

            stockCulture: stockProdItem?.product.variety.culture.id,
            stockVariety: stockProdItem?.product.variety.id,
            stockProduct: stockProdItem?.product.id,
            stockTray: stockProdItem?.tray.id,
            stockSeedSource: stockProdItem?.seedSource,
            stockCalculatedSeedQuantity: calculatedStockSeedQuantity,
          };

          return itemFormData;
        });

      setRowSelectedData(updatedRowSelectedData);
      updateOrderTotalPrice(updatedRowSelectedData);

      dispatch(setCulturesAction(culturesList.filter((culture) => !!culture) as Culture[]));
      dispatch(setProductsAction(productsList.filter((product) => !!product) as Product[]));

      formHandler.setFieldsValue({
        items: updatedFormItems,
      });
    } else {
      formHandler.setFieldsValue({
        items: [createDefaultRow()],
      });
    }
  // }, [initialOrderId]);
  }, [memoizedInitialOrder]);

  // Rows management

  const onRowAdded = (
    formAdd: (defaultValue?: any, index?: number) => void, // eslint-disable-line
    addedRowIndex: number,
  ) => {
    const updatedRowSelectedData: RowData[] = [
      ...rowSelectedData.slice(0, addedRowIndex),
      {
        scion: {
          culture: undefined,
          variety: undefined,
          product: undefined,
          tray: undefined,
        },
        stock: {
          culture: undefined,
          variety: undefined,
          product: undefined,
          tray: undefined,
        },
        totalPrice: undefined,
        stumpType: OrderStumpTypeEnum.DISCARD,
      },
    ];
    setRowSelectedData(updatedRowSelectedData);

    formAdd(createDefaultRow(), addedRowIndex);
  };

  const onRowRemoved = (
    formRemove: (index: number) => void,
    removedRowIndex: number,
  ) => {
    const { items: formItems } = formHandler.getFieldsValue();
    if (formItems.length < 2) {
      return;
    }
    const updatedRowSelectedData = [
      ...rowSelectedData.slice(0, removedRowIndex),
      ...rowSelectedData.slice(removedRowIndex + 1),
    ];
    setRowSelectedData(updatedRowSelectedData);
    updateOrderTotalPrice(updatedRowSelectedData);

    formRemove(removedRowIndex);
  };

  // Culture handling

  const onCultureSelect = (
    selectedCultureIndex: number,
    type: ProductTypeEnum.STOCK | ProductTypeEnum.SCION,
    culture: Culture,
  ) => {
    dispatch(cleanCulturesAction());

    const prevRowSelectedData = rowSelectedData[selectedCultureIndex];

    const updatedTypeInfo = type === ProductTypeEnum.SCION
      ? {
        scion: {
          ...prevRowSelectedData?.scion,
          culture,
          variety: undefined,
        },
      } : {
        stock: {
          ...prevRowSelectedData?.stock,
          culture,
          variety: undefined,
        },
      };
    const updatedRowSelectedData = [
      ...rowSelectedData.slice(0, selectedCultureIndex),
      {
        ...prevRowSelectedData,
        ...updatedTypeInfo,
      },
      ...rowSelectedData.slice(selectedCultureIndex + 1),
    ];

    setRowSelectedData(updatedRowSelectedData);
    updateOrderTotalPrice(updatedRowSelectedData);

    const { items: formItems } = formHandler.getFieldsValue();
    const updatedItems = [
      ...formItems.slice(0, selectedCultureIndex),
      {
        ...formItems[selectedCultureIndex],
        product: undefined,
        orderQuantity: undefined,
        plannedSeedDate: undefined,
        plannedDeliveryDate: undefined,
        calculatedSeedQuantity: undefined,
        unitPrice: undefined,
      },
      ...formItems.slice(selectedCultureIndex + 1),
    ];
    formHandler.setFieldsValue({
      items: updatedItems,
    });

    dispatch(getCultureProductsAction({
      cultureId: culture.id,
      filterData: {
        isPaginated: false,
        isActive: true,
      },
    }));
  };

  // Varieties handling
  const onProductSelect = (
    selectedProductIndex: number,
    type: ProductTypeEnum.STOCK | ProductTypeEnum.SCION,
    product?: Product,
  ) => {
    const prevRowSelectedData = rowSelectedData[selectedProductIndex];
    if (!prevRowSelectedData) {
      return;
    }
    const updatedTypeInfo = type === ProductTypeEnum.SCION
      ? {
        scion: {
          ...prevRowSelectedData.scion,
          variety: product?.variety,
          product,
        },
      } : {
        stock: {
          ...prevRowSelectedData.stock,
          variety: product?.variety,
          product,
        },
      };

    const updatedRowSelectedData = [
      ...rowSelectedData.slice(0, selectedProductIndex),
      {
        ...prevRowSelectedData,
        ...updatedTypeInfo,
      },
      ...rowSelectedData.slice(selectedProductIndex + 1),
    ];

    setRowSelectedData(updatedRowSelectedData);
  };

  // Tray options

  const trayEntities = useSelector(traysEntitiesSelector);
  const trayIds = useSelector(traysIdsSelector);
  const isLoadingTrays = useSelector(isLoadingTraysSelector);
  const trayList = trayIds
    .map((trayId) => trayEntities[trayId])
    .filter((tray) => !!tray) as Tray[];

  useEffect(() => {
    dispatch(getTrayListAction({
      isPaginated: false,
    }));
  }, []);

  const onTraySelect = (
    selectedTrayIndex: number,
    type: ProductTypeEnum.STOCK | ProductTypeEnum.SCION,
    tray?: Tray,
  ) => {
    const prevRowSelectedData = rowSelectedData[selectedTrayIndex];
    if (!prevRowSelectedData) {
      return;
    }
    const updatedTypeInfo = type === ProductTypeEnum.SCION
      ? {
        scion: {
          ...prevRowSelectedData.scion,
          tray,
        },
      } : {
        stock: {
          ...prevRowSelectedData.stock,
          tray,
        },
      };

    const updatedRowSelectedData = [
      ...rowSelectedData.slice(0, selectedTrayIndex),
      {
        ...prevRowSelectedData,
        ...updatedTypeInfo,
      },
      ...rowSelectedData.slice(selectedTrayIndex + 1),
    ];

    setRowSelectedData(updatedRowSelectedData);
  };

  // Stump Type

  const onStumpTypeSelect = (
    index: number,
    stumpType: OrderStumpTypeEnum,
  ) => {
    const currentFormItems: GraftingOrderItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const row = currentFormItems[index];

    if (!row) {
      return;
    }

    const prevRowSelectedData = rowSelectedData[index];

    const curQuantity = parseInt(row?.orderQuantity || '0', 10);
    const curUnitPrice = parseFloat(row.unitPrice || '0');

    const shouldResetStumpData = stumpType !== OrderStumpTypeEnum.STORE;

    const stumpQuantityUpdate = shouldResetStumpData
      ? {
        stumpQuantity: '',
        stumpUnitPrice: '',
      } : {
        stumpQuantity: row.stumpQuantity,
        stumpUnitPrice: row.stumpUnitPrice,
      };

    const updatedRowSelectedData = [
      ...rowSelectedData.slice(0, index),
      {
        ...prevRowSelectedData,
        stumpType,
        totalPrice: shouldResetStumpData
          ? (curQuantity * curUnitPrice)
          : prevRowSelectedData?.totalPrice,
      },
      ...rowSelectedData.slice(index + 1),
    ];

    setRowSelectedData(updatedRowSelectedData);
    updateOrderTotalPrice(updatedRowSelectedData);

    const updatedFormItems: typeof currentFormItems = [
      ...currentFormItems.slice(0, index),
      {
        ...row,
        ...stumpQuantityUpdate,
      },
      ...currentFormItems.slice(index + 1),
    ];

    formHandler.setFields([{
      name: 'items',
      value: updatedFormItems,
    }]);
  };

  // Date range calculation

  const onDateChange = (index: number, field?: 'seed' | 'delivery') => {
    const currentFormItems: GraftingOrderItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const row = currentFormItems[index];

    if (!row) {
      return;
    }

    const curScionProduct = rowSelectedData[index]?.scion?.product;
    const curStockProduct = rowSelectedData[index]?.stock?.product;

    if (
      !curScionProduct
      || !curScionProduct.periodFormation
      || !curStockProduct
      || !curStockProduct.periodFormation
    ) {
      return;
    }

    const longerPeriodFormation = curScionProduct.periodFormation >= curStockProduct.periodFormation
      ? curScionProduct.periodFormation
      : curStockProduct.periodFormation;

    const updatedDates = field === 'delivery'
      ? {
        plannedSeedDate: row
          .plannedDeliveryDate
          ?.subtract(longerPeriodFormation, 'day'),
        plannedDeliveryDate: row
          .plannedDeliveryDate,
      } : {
        plannedSeedDate: row
          .plannedSeedDate,
        plannedDeliveryDate: row
          .plannedSeedDate
          ?.add(longerPeriodFormation, 'day'),
      };

    const updatedFormItems: typeof currentFormItems = [
      ...currentFormItems.slice(0, index),
      {
        ...row,
        ...updatedDates,
      },
      ...currentFormItems.slice(index + 1),
    ];

    formHandler.setFields([{
      name: 'items',
      value: updatedFormItems,
    }]);
  };

  const updateSeedQuantity = (
    index: number,
  ) => {
    const currentFormItems: GraftingOrderItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const row = currentFormItems[index];

    if (!row) {
      return;
    }

    const { stumpType } = row;

    const getFormattedTotal = (
      curProduct?: Product,
      curTray?: Tray,
      curUnit?: OrderUnitEnum,
      curQuantity?: string,
    ): string | undefined => {
      if (!curProduct || !curTray || !curUnit || curQuantity === undefined) {
        return undefined;
      }

      if (!curProduct) {
        return undefined;
      }

      const calculatedTotal = calculateSeedQuantity(
        parseInt(curQuantity, 10),
        curUnit,
        curProduct,
        curTray,
      );

      const typeTotal = (
        stumpType === OrderStumpTypeEnum.GRAFT
        && curProduct.type === ProductTypeEnum.SCION
      )
        ? calculatedTotal / 2
        : calculatedTotal;

      const formattedCalculatedTotal = `${
        Intl
          .NumberFormat('pt-BR')
          .format(typeTotal)}`;

      return formattedCalculatedTotal;
    };

    const updatedFormItems: typeof currentFormItems = [
      ...currentFormItems.slice(0, index),
      {
        ...row,
        scionCalculatedSeedQuantity: getFormattedTotal(
          rowSelectedData[index]?.scion?.product,
          trayEntities[row?.scionTray || ''],
          row.unit,
          row.orderQuantity,
        ) || '',
        stockCalculatedSeedQuantity: getFormattedTotal(
          rowSelectedData[index]?.stock?.product,
          trayEntities[row?.stockTray || ''],
          row.unit,
          row.orderQuantity,
        ) || '',
      },
      ...currentFormItems.slice(index + 1),
    ];

    formHandler.setFields([{
      name: 'items',
      value: updatedFormItems,
    }]);
  };

  // Price handling
  const updateTotalPrice = (index: number) => {
    const currentFormItems: GraftingOrderItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const row = currentFormItems[index];
    if (!row) {
      return;
    }

    const curQuantity = parseInt(row?.orderQuantity || '0', 10);
    const curUnitPrice = parseFloat(row.unitPrice || '0');

    if (!curQuantity || !curUnitPrice) {
      return;
    }

    const curStumpQuantity = parseInt(row?.stumpQuantity || '0', 10);
    const curStumpUnitPrice = parseFloat(row.stumpUnitPrice || '0');
    const updatedStump = curStumpQuantity * curStumpUnitPrice;

    const updatedTotalPrice = (curQuantity * curUnitPrice) + updatedStump;

    const prevRowSelectedData = rowSelectedData[index];

    const updatedRowSelectedData = [
      ...rowSelectedData.slice(0, index),
      {
        ...prevRowSelectedData,
        totalPrice: updatedTotalPrice,
      },
      ...rowSelectedData.slice(index + 1),
    ];

    setRowSelectedData(updatedRowSelectedData);

    // const updatedOrderTotalPrice = updatedRowSelectedData
    //   .reduce((acc, curRow) => {
    //     const newValue = (acc || 0) + (curRow.totalPrice || 0);
    //     return newValue;
    //   }, 0);

    // setOrderTotalPrice(updatedOrderTotalPrice || 0);
    updateOrderTotalPrice(updatedRowSelectedData);
  };

  const isPreRequisiteFilled = (rowIndex: number) => (
    rowSelectedData[rowIndex]?.scion?.culture
    && rowSelectedData[rowIndex]?.scion?.variety
    && rowSelectedData[rowIndex]?.scion?.tray
  ) && (
    rowSelectedData[rowIndex]?.stock?.culture
    && rowSelectedData[rowIndex]?.stock?.variety
    && rowSelectedData[rowIndex]?.stock?.tray
  );

  const isStumpEnabled = (rowIndex: number) => (
    rowSelectedData[rowIndex]?.stumpType === OrderStumpTypeEnum.STORE
  );

  const isActiveOrderCanceled = initialOrder?.status === OrderOtherStatusEnum.CANCELED;

  return (
    <FormContainer
      layout="vertical"
      form={formHandler}
      validateTrigger="onSubmit"
    >
      <Form.List
        name="items"
        rules={[{
          message: 'Insira pelo menos um item',
          validator: () => {
            // TODO: check this logic
            const formItems = [1];
            if (formItems.length > 0) {
              return Promise.resolve();
            }

            return Promise.reject();
          },
        }]}
      >
        {(formListItems, { add, remove }) => (
          <GraftingOrderTable
            dataSource={formListItems}
            tableLayout="fixed"
            scroll={{ // TODO: adjust vertical scrolling
              x: true,
              y: 390,
            }}
            bordered
            pagination={false}
            footer={() => (
              <TableFooterContainer>
                <AddRowButton
                  icon={<PlusOutlined />}
                  onClick={() => onRowAdded(add, formListItems.length)}
                  disabled={externalLoading}
                >
                  Adicionar item
                </AddRowButton>
                <OrderPriceContainer>
                  <span>
                    {`Total orçamento: ${Intl
                      .NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })
                      .format(orderTotalPrice || 0)
                    }`}
                  </span>
                  <span>
                    {`Adiantamento: ${Intl
                      .NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })
                      .format((orderTotalPrice || 0) / 2)
                    }`}
                  </span>
                </OrderPriceContainer>
              </TableFooterContainer>
            )}
          >
            <Table.Column
              title=""
              render={(cellValue, rowRecord, rowIndex) => (
                <RowInfoContainer>
                  <RemoveRowButton
                    disabled={externalLoading || isActiveOrderCanceled}
                    onClick={() => onRowRemoved(remove, rowIndex)}
                  >
                    <RemoveRowIcon />
                  </RemoveRowButton>
                  <RowIndexLabel>
                    {`#${rowIndex + 1}`}
                  </RowIndexLabel>
                </RowInfoContainer>
              )}
            />
            <Table.ColumnGroup
              title="Enxerto"
            >
              <Table.Column
                title="Cultura"
                render={(cellValue, rowRecord, rowIndex) => (
                  <CultureSelectSearch
                    form={{
                      FormItem: RowFormItem,
                      formItemProps: {
                        name: [rowIndex, 'scionCulture'],
                        rules: [{
                          required: true,
                          message: 'Selecione uma cultura',
                        }],
                      },
                    }}
                    onSelect={(culture) => onCultureSelect(
                      rowIndex,
                      ProductTypeEnum.SCION,
                      culture,
                    )}
                    disabled={externalLoading || isActiveOrderCanceled}
                    initialSelected={rowSelectedData[rowIndex]?.scion?.culture}
                  />
                )}
              />
              <Table.Column
                title="Variedade"
                render={(cellValue, rowRecord, rowIndex) => (
                  <>
                    <CultureProductSelect
                      cultureId={rowSelectedData[rowIndex]?.scion?.culture?.id}
                      autoFetch={!initialOrder}
                      form={{
                        FormItem: RowFormItem,
                        formItemProps: {
                          name: [rowIndex, 'scionProduct'],
                          rules: [{
                            required: true,
                            message: 'Selecione uma variedade',
                          }],
                        },
                      }}
                      onSelect={(product) => onProductSelect(
                        rowIndex,
                        ProductTypeEnum.SCION,
                        product,
                      )}
                      filter={{
                        productType: ProductTypeEnum.SCION,
                      }}
                      disabled={externalLoading || isActiveOrderCanceled}
                      initialSelected={rowSelectedData[rowIndex]?.scion?.product}
                    />
                  </>
                )}
              />
              <Table.Column
                title="Bdj"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'scionTray']}
                    rules={[
                      {
                        required: true,
                        message: 'Selecione uma bandeja',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecione uma bandeja"
                      onSelect={(trayId) => onTraySelect(
                        rowIndex,
                        ProductTypeEnum.SCION,
                        trayEntities[trayId],
                      )}
                      onChange={() => updateSeedQuantity(rowIndex)}
                      disabled={isLoadingTrays || externalLoading || isActiveOrderCanceled}
                      notFoundContent={<div>Nenhuma bandeja disponível</div>}
                    >
                      {trayList.map((trayOption) => (
                        <Select.Option
                          value={trayOption.id}
                          key={trayOption.id}
                        >
                          {`${trayOption.capacity}${trayOption.isHydroponic ? ' Hidro' : ''}`}
                        </Select.Option>
                      ))}
                    </Select>
                  </RowFormItem>
                )}
              />
              <Table.Column
                title="Semente"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'scionSeedSource']}
                    required
                  >
                    <Select
                      placeholder="Selecione um estoque de semente"
                      notFoundContent={null}
                      disabled={
                        externalLoading
                        || !rowSelectedData[rowIndex]?.scion?.culture
                        || !rowSelectedData[rowIndex]?.scion?.variety
                        || !rowSelectedData[rowIndex]?.scion?.tray
                        || isActiveOrderCanceled
                      }
                    >
                      {Object.values(InventoryTypeEnum).map((inventoryType) => (
                        <Select.Option
                          value={inventoryType}
                          key={inventoryType}
                        >
                          {InventoryTypeDisplayMapping[inventoryType].name}
                        </Select.Option>
                      ))}
                    </Select>
                  </RowFormItem>
                )}
              />
            </Table.ColumnGroup>
            <Table.ColumnGroup
              title="Porta-Enxerto"
            >
              <Table.Column
                title="Cultura"
                render={(cellValue, rowRecord, rowIndex) => (
                  <CultureSelectSearch
                    form={{
                      FormItem: RowFormItem,
                      formItemProps: {
                        name: [rowIndex, 'stockCulture'],
                        rules: [{
                          required: true,
                          message: 'Selecione uma cultura',
                        }],
                      },
                    }}
                    onSelect={(culture) => onCultureSelect(
                      rowIndex,
                      ProductTypeEnum.STOCK,
                      culture,
                    )}
                    disabled={externalLoading || isActiveOrderCanceled}
                    initialSelected={rowSelectedData[rowIndex]?.stock?.culture}
                  />
                )}
              />
              <Table.Column
                title="Variedade"
                render={(cellValue, rowRecord, rowIndex) => (
                  <>
                    <CultureProductSelect
                      cultureId={rowSelectedData[rowIndex]?.stock?.culture?.id}
                      autoFetch={!initialOrder}
                      form={{
                        FormItem: RowFormItem,
                        formItemProps: {
                          name: [rowIndex, 'stockProduct'],
                          rules: [{
                            required: true,
                            message: 'Selecione uma variedade',
                          }],
                        },
                      }}
                      onSelect={(product) => onProductSelect(
                        rowIndex,
                        ProductTypeEnum.STOCK,
                        product,
                      )}
                      filter={{
                        productType: ProductTypeEnum.STOCK,
                      }}
                      disabled={externalLoading || isActiveOrderCanceled}
                      initialSelected={rowSelectedData[rowIndex]?.stock?.product}
                    />
                  </>
                )}
              />
              <Table.Column
                title="Bdj"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'stockTray']}
                    rules={[
                      {
                        required: true,
                        message: 'Selecione uma bandeja',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecione uma bandeja"
                      onSelect={(trayId) => onTraySelect(
                        rowIndex,
                        ProductTypeEnum.STOCK,
                        trayEntities[trayId],
                      )}
                      onChange={() => updateSeedQuantity(rowIndex)}
                      disabled={isLoadingTrays || externalLoading || isActiveOrderCanceled}
                      notFoundContent={<div>Nenhuma bandeja disponível</div>}
                    >
                      {trayList.map((trayOption) => (
                        <Select.Option
                          value={trayOption.id}
                          key={trayOption.id}
                        >
                          {`${trayOption.capacity}${trayOption.isHydroponic ? ' Hidro' : ''}`}
                        </Select.Option>
                      ))}
                    </Select>
                  </RowFormItem>
                )}
              />
              <Table.Column
                title="Semente"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'stockSeedSource']}
                    required
                  >
                    <Select
                      placeholder="Selecione um estoque de semente"
                      notFoundContent={null}
                      disabled={
                        externalLoading
                        || !rowSelectedData[rowIndex]?.stock?.culture
                        || !rowSelectedData[rowIndex]?.stock?.variety
                        || !rowSelectedData[rowIndex]?.stock?.tray
                        || isActiveOrderCanceled
                      }
                    >
                      {Object.values(InventoryTypeEnum).map((inventoryType) => (
                        <Select.Option
                          value={inventoryType}
                          key={inventoryType}
                        >
                          {InventoryTypeDisplayMapping[inventoryType].name}
                        </Select.Option>
                      ))}
                    </Select>
                  </RowFormItem>
                )}
              />
            </Table.ColumnGroup>
            <Table.ColumnGroup
              title="Muda enxertada"
            >
              <Table.Column
                title={<TableHeaderTitle>Despontar</TableHeaderTitle>}
                ellipsis
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'pruning']}
                    valuePropName="checked"
                    size={60}
                  >
                    <Checkbox
                      disabled={
                        externalLoading || !isPreRequisiteFilled(rowIndex) || isActiveOrderCanceled
                      }
                    />
                  </RowFormItem>
                )}
              />
              <Table.Column
                title="Unidade"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'unit']}
                    required
                  >
                    <Select
                      placeholder="Selecione uma unidade"
                      notFoundContent={null}
                      disabled={
                        externalLoading || !isPreRequisiteFilled(rowIndex) || isActiveOrderCanceled
                      }
                      onChange={() => updateSeedQuantity(rowIndex)}
                    >
                      {Object
                        .values(OrderUnitEnum)
                        .filter((curUnit) => curUnit !== OrderUnitEnum.UNIT)
                        .map((curUnit) => (
                          <Select.Option
                            value={curUnit}
                            key={curUnit}
                          >
                            {OrderUnitDisplayMapping[curUnit].name}
                          </Select.Option>
                        ))}
                    </Select>
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="Qtd"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'orderQuantity']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma quantidade',
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      min={1}
                      onChange={() => {
                        updateSeedQuantity(rowIndex);
                        updateTotalPrice(rowIndex);
                      }}
                      disabled={
                        externalLoading || !isPreRequisiteFilled(rowIndex) || isActiveOrderCanceled
                      }
                    />
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title={<div>Tocos</div>}
                ellipsis
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'stumpType']}
                  >
                    <Select
                      disabled={
                        externalLoading || !isPreRequisiteFilled(rowIndex) || isActiveOrderCanceled
                      }
                      onSelect={(
                        stumpType: OrderStumpTypeEnum,
                      ) => {
                        onStumpTypeSelect(rowIndex, stumpType);
                        updateSeedQuantity(rowIndex);
                      }}
                    >
                      {
                        Object
                          .values(OrderStumpTypeEnum)
                          .map((stumpType) => {
                            const display = OrderStumpTypeDisplayMapping[stumpType].name;
                            return (
                              <Select.Option
                                value={stumpType}
                                key={stumpType}
                              >
                                { display }
                              </Select.Option>
                            );
                          })
                      }
                    </Select>
                  </RowFormItem>
                )}
              />
              <Table.Column
                title="Qtd Tocos"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'stumpQuantity']}
                    rules={[
                      {
                        required: isStumpEnabled(rowIndex),
                        message: 'Insira uma quantidade',
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      min={1}
                      onChange={() => {
                        updateSeedQuantity(rowIndex);
                        updateTotalPrice(rowIndex);
                      }}
                      disabled={
                        externalLoading
                        || !isPreRequisiteFilled(rowIndex)
                        || !isStumpEnabled(rowIndex)
                        || isActiveOrderCanceled
                      }
                    />
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="Data semeio prev"
                width={180}
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'plannedSeedDate']}
                    // initialValue={dayjs()}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma data',
                      },
                    ]}
                  >
                    <DatePicker
                      disabled={
                        externalLoading || !isPreRequisiteFilled(rowIndex) || isActiveOrderCanceled
                      }
                      onChange={() => onDateChange(rowIndex, 'seed')}
                    />
                  </RowFormItem>
                )}
              />
              <Table.Column
                title="Data entrega prev"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'plannedDeliveryDate']}
                    // initialValue={dayjs()}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma data',
                      },
                    ]}
                  >
                    <DatePicker
                      onChange={() => onDateChange(rowIndex, 'delivery')}
                      disabled={
                        externalLoading || !isPreRequisiteFilled(rowIndex) || isActiveOrderCanceled
                      }
                    />
                  </RowFormItem>
                )}
              />
              <Table.Column
                title="Qtd semente prev (Enx)"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'scionCalculatedSeedQuantity']}
                  >
                    <Input type="text" disabled />
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="Qtd semente prev (Porta)"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'stockCalculatedSeedQuantity']}
                  >
                    <Input type="text" disabled />
                  </RowSmallFormItem>
                )}
              />
            </Table.ColumnGroup>
            <Table.ColumnGroup
              title="Orçamento"
            >

              <Table.Column
                title="$Unit"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'unitPrice']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira um valor',
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      prefix="R$"
                      onChange={() => updateTotalPrice(rowIndex)}
                      disabled={
                        externalLoading || !isPreRequisiteFilled(rowIndex) || isActiveOrderCanceled
                      }
                    />
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="$Unit Toco"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem
                    name={[rowIndex, 'stumpUnitPrice']}
                    rules={[
                      {
                        required: isStumpEnabled(rowIndex),
                        message: 'Insira um valor',
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      prefix="R$"
                      onChange={() => updateTotalPrice(rowIndex)}
                      disabled={
                        externalLoading
                        || !isPreRequisiteFilled(rowIndex)
                        || !isStumpEnabled(rowIndex)
                        || isActiveOrderCanceled
                      }
                    />
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="$Total"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowSmallFormItem>
                    <Input
                      type="text"
                      prefix="R$"
                      disabled
                      value={
                        Intl
                          .NumberFormat('pt-BR')
                          .format(rowSelectedData[rowIndex]?.totalPrice || 0)
                      }
                    />
                  </RowSmallFormItem>
                )}
              />
              <Table.Column
                title="Obs"
                render={(cellValue, rowRecord, rowIndex) => (
                  <RowFormItem
                    name={[rowIndex, 'notes']}
                  >
                    <NotesInput
                      disabled={
                        externalLoading || !isPreRequisiteFilled(rowIndex) || isActiveOrderCanceled
                      }
                    />
                  </RowFormItem>
                )}
              />
            </Table.ColumnGroup>
          </GraftingOrderTable>
        )}
      </Form.List>
    </FormContainer>
  );
};

export default GraftingTable;
